import React, {
  memo,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
  useEffect
} from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { format, endOfMonth } from 'date-fns'
import _ from 'lodash'

import { TimeslotButton } from 'components/Common'
import Divider from 'components/Appointments/AppointmentBookingModal/Divider'
import { Next, Prev } from 'components/Icons'
import Loading from 'components/Loading'

import {
  getProvider,
  convertToProviderTimezone,
  getMonthYears,
  getAvailableTimeslots,
  isNullOrUndefined,
  mergeStyles
} from '@enlyt/base'
import {
  ADDITIONAL_DAY_FOR_RANGE,
  AVAILABLE_DAYS_FOR_REQUEST
} from '../AppointmentBookingModal'

import defaultStyles from './styles'
import cssStyles from './TimeSlotCarousel.module.css'

const DayStatus = {
  SELECTED: 1,
  ACTIVE: 2,
  INACTIVE: 3
}

const monthYears = getMonthYears()

const DATE_FORMAT = 'yyyy-MM-dd'
const NO_AVAILABLE_TIMESLOTS_TEXT =
  'There are no time slots available. Please choose another day or edit your choice of Practice Location / Reason for Visit.'
const NAMES_OF_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

const TimeSlotCarousel = forwardRef((props, ref) => {
  const {
    loadingOtherTimeSlots,
    selectedMonthIndex,
    getFirstPartNewTimeSlotsForMonth,
    getSecondPartNewTimeSlotsForMonth,
    errorOtherTimeSlots,
    selectTimeSlot,
    selectedTimeslotId,
    selectedDate,
    setSelectedDate,
    selectedDayIndex,
    setSelectedDayIndex,
    setSelectedMonthIndex,
    activeDateSlideIndex,
    setActiveDateSlideIndex,
    availableDaysInCurrentMonth,
    setAvailableDaysInCurrentMonth,
    timeslots
  } = props
  const styles = mergeStyles(defaultStyles, props.style)

  const [monthEmblaRef, monthEmblaApi] = useEmblaCarousel({
    align: 'start',
    loop: false,
    skipSnaps: false
  })

  const [daysEmblaRef, daysEmblaApi] = useEmblaCarousel({
    align: 'start',
    loop: false,
    skipSnaps: false
  })

  const currentDate = useMemo(
    () => new Date(convertToProviderTimezone()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const currentDateNumber = useMemo(
    () => currentDate.getDate(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    monthEmblaApi?.scrollTo(selectedMonthIndex, true)
  }, [monthEmblaApi, selectedMonthIndex])

  useEffect(() => {
    daysEmblaApi?.scrollTo(activeDateSlideIndex, true)
  }, [daysEmblaApi, activeDateSlideIndex, availableDaysInCurrentMonth.length])

  const clearSelectedDate = () => {
    setSelectedDate(null)
    setSelectedDayIndex(null)
    selectTimeSlot(null)
  }

  const weekIndexHandler = index => {
    if (
      activeDateSlideIndex + index < 0 ||
      activeDateSlideIndex + index > availableDaysInCurrentMonth.length
    ) {
      return
    }

    if (
      activeDateSlideIndex + index <= availableDaysInCurrentMonth.length &&
      activeDateSlideIndex + index >= 0
    ) {
      clearSelectedDate()
      daysEmblaApi?.scrollTo(activeDateSlideIndex + index, true)
      setActiveDateSlideIndex(activeDateSlideIndex + index)
    }
  }

  const getNewSlotsForTwoRequests = useCallback(
    async (firstDates, lastDates) => {
      const provider = await getProvider()
      Promise.all([
        getFirstPartNewTimeSlotsForMonth({
          variables: { ...provider, ...firstDates }
        }),
        getSecondPartNewTimeSlotsForMonth({
          variables: { ...provider, ...lastDates }
        })
      ]).catch(err =>
        console.log('getting TimeSlots for two requests error', err)
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const onMonthChange = newIndex => {
    setSelectedMonthIndex(newIndex)
    clearSelectedDate()

    setActiveDateSlideIndex(0)
    daysEmblaApi?.scrollTo(0, true)

    const isCurrentMonth = currentDate.getMonth() === monthYears[newIndex].month
    const lastMonthDateNumber =
      endOfMonth(
        new Date(monthYears[newIndex].year, monthYears[newIndex].month)
      ).getDate() + ADDITIONAL_DAY_FOR_RANGE

    if (isCurrentMonth) {
      setAvailableDaysInCurrentMonth(
        _.range(currentDateNumber, lastMonthDateNumber, 1)
      )
    } else {
      setAvailableDaysInCurrentMonth(_.range(1, lastMonthDateNumber, 1))
    }

    return isCurrentMonth
  }

  const onMonthNext = async () => {
    onMonthChange(selectedMonthIndex + 1)

    const currentYear = monthYears[selectedMonthIndex + 1].year
    const nextMonth = monthYears[selectedMonthIndex + 1].month
    const monthLastDayNumber = endOfMonth(
      new Date(currentYear, nextMonth)
    ).getDate()

    const firstDates = {
      startDate: format(new Date(currentYear, nextMonth, 1), DATE_FORMAT),
      endDate: format(
        new Date(currentYear, nextMonth, AVAILABLE_DAYS_FOR_REQUEST),
        DATE_FORMAT
      )
    }

    const lastDates = {
      startDate: format(
        new Date(currentYear, nextMonth, AVAILABLE_DAYS_FOR_REQUEST + 1),
        DATE_FORMAT
      ),
      endDate: format(
        new Date(currentYear, nextMonth, monthLastDayNumber),
        DATE_FORMAT
      )
    }

    await getNewSlotsForTwoRequests(firstDates, lastDates)
  }

  const onMonthBack = async (isGoToFirstDate = false) => {
    const isCurrentMonth = isGoToFirstDate
      ? onMonthChange(0)
      : onMonthChange(selectedMonthIndex - 1)

    const previousMonth = isGoToFirstDate
      ? monthYears[0].month
      : monthYears[selectedMonthIndex - 1].month
    const currentYear = isGoToFirstDate
      ? monthYears[0].year
      : monthYears[selectedMonthIndex - 1].year
    const monthLastDayNumber = endOfMonth(
      new Date(currentYear, previousMonth)
    ).getDate()

    if (isCurrentMonth) {
      const differenceBetweenDays = monthLastDayNumber - currentDateNumber
      const isMoreThanAvailableNumberOfDaysForRequest =
        differenceBetweenDays > AVAILABLE_DAYS_FOR_REQUEST

      if (isMoreThanAvailableNumberOfDaysForRequest) {
        try {
          const lastDayNumberForFirstDates =
            AVAILABLE_DAYS_FOR_REQUEST / 2 + currentDateNumber

          const firstDates = {
            startDate: format(
              new Date(currentYear, previousMonth, currentDateNumber),
              DATE_FORMAT
            ),
            endDate: format(
              new Date(currentYear, previousMonth, lastDayNumberForFirstDates),
              DATE_FORMAT
            )
          }

          const lastDates = {
            startDate: format(
              new Date(
                currentYear,
                previousMonth,
                lastDayNumberForFirstDates + 1
              ),
              DATE_FORMAT
            ),
            endDate: format(
              new Date(currentYear, previousMonth, monthLastDayNumber),
              DATE_FORMAT
            )
          }

          await getNewSlotsForTwoRequests(firstDates, lastDates)
        } catch (e) {
          console.log('getting TimeSlots error for two requests', e)
        }
      } else {
        try {
          const firstDates = {
            startDate: format(
              new Date(currentYear, previousMonth, currentDateNumber),
              DATE_FORMAT
            ),
            endDate: format(
              new Date(currentYear, previousMonth, monthLastDayNumber),
              DATE_FORMAT
            )
          }

          const provider = await getProvider()
          await getFirstPartNewTimeSlotsForMonth({
            variables: { ...provider, ...firstDates }
          })
        } catch (e) {
          console.log('getting TimeSlots error', e)
        }
      }

      return
    }

    try {
      const firstDates = {
        startDate: format(new Date(currentYear, previousMonth, 1), DATE_FORMAT),
        endDate: format(
          new Date(currentYear, previousMonth, AVAILABLE_DAYS_FOR_REQUEST),
          DATE_FORMAT
        )
      }

      const lastDates = {
        startDate: format(
          new Date(currentYear, previousMonth, AVAILABLE_DAYS_FOR_REQUEST + 1),
          DATE_FORMAT
        ),
        endDate: format(
          new Date(currentYear, previousMonth, monthLastDayNumber),
          DATE_FORMAT
        )
      }

      await getNewSlotsForTwoRequests(firstDates, lastDates)
    } catch (e) {
      console.log('getting TimeSlots error for two requests', e)
    }
  }

  const selectedDayHandler = dayIndex => {
    if (
      isNullOrUndefined(selectedMonthIndex) ||
      selectedDayIndex === dayIndex
    ) {
      return
    }

    const newSelectedDate = new Date(
      monthYears[selectedMonthIndex].year,
      monthYears[selectedMonthIndex].month,
      availableDaysInCurrentMonth[dayIndex]
    )

    setSelectedDate(newSelectedDate)
    setSelectedDayIndex(dayIndex)

    selectTimeSlot(null)
  }

  useImperativeHandle(ref, () => ({
    clearSelectedDate: () => {
      clearSelectedDate()
    },
    goToCurrentDate: () => {
      onMonthBack(true).catch(e => console.log('Go to current date error ', e))
    }
  }))

  const renderTimeSlots = () => {
    if (errorOtherTimeSlots) {
      return <p>Something went wrong</p>
    }

    if (!selectedDate) {
      return
    }

    const slots = getAvailableTimeslots(timeslots, selectedDate)

    if (!slots.length) {
      return (
        <p style={styles.noAvailableTimeslotText}>
          {NO_AVAILABLE_TIMESLOTS_TEXT}
        </p>
      )
    }

    return slots.map(timeslot => (
      <TimeslotButton
        key={timeslot.id}
        id={timeslot.id}
        isSelected={selectedTimeslotId === timeslot.id}
        onClick={selectTimeSlot}
      >
        {format(timeslot.date, 'h:mm a')}
      </TimeslotButton>
    ))
  }

  const getDayStyles = dayStatus => {
    return (
      (dayStatus === DayStatus.SELECTED && styles.daySelected) ||
      (dayStatus === DayStatus.ACTIVE && styles.dayNotSelected)
    )
  }

  const getDayOfWeekStyles = dayStatus => {
    return (
      (dayStatus === DayStatus.SELECTED && styles.dayOfWeekSelected) ||
      (dayStatus === DayStatus.ACTIVE && styles.dayOfWeekNotSelected)
    )
  }

  const getDayStatus = date => {
    if (
      !isNullOrUndefined(selectedDayIndex) &&
      availableDaysInCurrentMonth[selectedDayIndex] === date.getDate()
    ) {
      return DayStatus.SELECTED
    }
    return DayStatus.ACTIVE
  }

  return (
    <>
      {loadingOtherTimeSlots && <Loading />}
      <div style={styles.section}>
        <div style={styles.carousel}>
          <button
            type='button'
            onClick={() => onMonthBack(false)}
            style={styles.button}
            aria-label='previous month'
          >
            <Prev />
          </button>
          <div
            style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}
            ref={monthEmblaRef}
          >
            <div style={{ display: 'flex' }}>
              {monthYears.map((my, i) => (
                <div key={i} style={{ flex: '0 0 100%', minWidth: 0 }}>
                  <div style={styles.monthYear}>
                    {`${NAMES_OF_MONTHS[my.month]} ${my.year}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button
            type='button'
            onClick={onMonthNext}
            style={styles.button}
            aria-label='next month'
          >
            <Next />
          </button>
        </div>
      </div>

      <Divider />
      <div style={styles.section}>
        <div style={{ ...styles.carousel, ...styles.dayCarousel }}>
          <button
            type='button'
            onClick={() => weekIndexHandler(-7)}
            style={styles.button}
            aria-label='previous week'
          >
            <Prev />
          </button>
          <div
            style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}
            ref={daysEmblaRef}
          >
            <div
              style={{ ...styles.slider, ...styles.daySlider, display: 'flex' }}
            >
              {availableDaysInCurrentMonth.map((dayNumber, i) => {
                const date = new Date(
                  monthYears[selectedMonthIndex].year,
                  monthYears[selectedMonthIndex].month,
                  dayNumber
                )

                const dayStatus = getDayStatus(date)

                return (
                  <div
                    key={i}
                    onClick={() => selectedDayHandler(i)}
                    className={cssStyles.slideOverride}
                    style={{
                      ...styles.dayContainer,
                      flex: '0 0 14.28%',
                      minWidth: 0
                    }}
                  >
                    <div style={getDayOfWeekStyles(dayStatus)}>
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div style={getDayStyles(dayStatus)}>
                      {format(date, 'd')}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          <button
            type='button'
            onClick={() => weekIndexHandler(7)}
            style={styles.button}
            aria-label='next week'
          >
            <Next />
          </button>
        </div>
        <div style={styles.timeslotsContainer}>{renderTimeSlots()}</div>
      </div>
    </>
  )
})

TimeSlotCarousel.displayName = 'TimeSlotCarousel'

export default memo(TimeSlotCarousel)
